export interface SseClientOptions {
    url: string;
    token: string;
    events: {
        connected: string;
        notification: string;
        heartbeat: string;
    };
    onNotification: (data: Record<string, unknown>) => void;
    onConnected?: (data: Record<string, unknown>) => void;
    onError?: (error: unknown) => void;
}

export interface SseClient {
    close: () => void;
}

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

function parseSseBlock(block: string): { event: string; data: string } | null {
    let event = "message";
    const dataLines: string[] = [];

    for (const line of block.split("\n")) {
        if (!line || line.startsWith(":")) continue;
        if (line.startsWith("event:")) {
            event = line.slice(6).trim();
            continue;
        }
        if (line.startsWith("data:")) {
            dataLines.push(line.slice(5).trimStart());
        }
    }

    if (dataLines.length === 0) return null;
    return { event, data: dataLines.join("\n") };
}

export function createSseClient(options: SseClientOptions): SseClient {
    let aborted = false;
    let reconnectAttempt = 0;
    let reconnectTimer: number | undefined;
    let currentAbort: AbortController | null = null;

    const scheduleReconnect = () => {
        if (aborted) return;
        const delay = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempt, RECONNECT_MAX_MS);
        reconnectAttempt += 1;
        reconnectTimer = window.setTimeout(() => {
            void connect();
        }, delay);
    };

    const handleBlock = (block: string) => {
        const parsed = parseSseBlock(block);
        if (!parsed?.data) return;

        let payload: Record<string, unknown>;
        try {
            payload = JSON.parse(parsed.data) as Record<string, unknown>;
        } catch {
            return;
        }

        if (parsed.event === options.events.connected) {
            options.onConnected?.(payload);
            return;
        }

        if (parsed.event === options.events.notification) {
            options.onNotification(payload);
        }
    };

    const connect = async () => {
        if (aborted) return;

        currentAbort?.abort();
        currentAbort = new AbortController();

        try {
            const response = await fetch(options.url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${options.token}`,
                    Accept: "text/event-stream",
                },
                signal: currentAbort.signal,
            });

            if (!response.ok || !response.body) {
                throw new Error(`SSE connection failed (${response.status})`);
            }

            reconnectAttempt = 0;
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (!aborted) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                let separatorIndex = buffer.indexOf("\n\n");
                while (separatorIndex !== -1) {
                    const block = buffer.slice(0, separatorIndex);
                    buffer = buffer.slice(separatorIndex + 2);
                    handleBlock(block);
                    separatorIndex = buffer.indexOf("\n\n");
                }
            }
        } catch (error) {
            if (aborted || (error as Error).name === "AbortError") return;
            options.onError?.(error);
        }

        if (!aborted) {
            scheduleReconnect();
        }
    };

    void connect();

    return {
        close: () => {
            aborted = true;
            if (reconnectTimer !== undefined) {
                clearTimeout(reconnectTimer);
            }
            currentAbort?.abort();
            currentAbort = null;
        },
    };
}
